import classNames from "classnames";
import type { ArticlePreview } from "../../common/types/Storyblok";
import "./FullArticle.css";
import { Tag, TagCategory, TagCategoryToDisplayName } from "@albertine/types";
import { tagDisplayName } from "albertine-shared-web";
import { cdnImage } from "../../common/utils/image.util";
import { separateTagsByCategory, sortTags } from "../../common/utils/tags.util";
import { isTypeOf } from "../../common/utils/zod.util";
import { TagToJSXElement } from "../CatalogBeta/TagsWithCategories";

type FullArticleProps = {
    blok: ArticlePreview;
};

function FullArticle(props: FullArticleProps) {
    const { blok } = props;

    const place = (blok.place && blok.place[0]) || undefined;

    const sortedTags = sortTags(blok.tags ?? []);
    const separatedTags = Object.entries(separateTagsByCategory(sortedTags));

    const dynamicLeftIndent =
        blok.headline && blok.headline.length > 0 ? "left-indent" : "";

    return (
        <div className="article-full">
            <div className="article-full__header top-gutter-widest">
                <div className="heading1">{blok.title}</div>
                <div className="infoText">{blok.keyDetail}</div>
            </div>
            <div className="article-full__mainContent">
                <div className="article-full__images bottom-gutter-narrow">
                    <img
                        src={cdnImage(blok.coverImage ? blok.coverImage.filename : "", {
                            height: 400,
                            width: 500,
                        })}
                        alt={blok.title}
                    />
                    {blok.additionalImages &&
                        blok.additionalImages.map((image) =>
                            image ? (
                                <img
                                    src={cdnImage(image.filename)}
                                    alt={blok.title}
                                    key={`${image.filename}__image`}
                                />
                            ) : null,
                        )}
                </div>

                {blok.headline && blok.headline.length > 0 && (
                    <div className="article-full__content paragraphMediumLarge top-gutter-wide bottom-gutter-mid">
                        {blok.headline}
                    </div>
                )}

                <div
                    className={`article-full__content paragraph top-gutter-mid bottom-gutter-mid ${dynamicLeftIndent}`}
                >
                    {blok.content &&
                        blok.content.split("\n").map((p, index) => (
                            <p key={`${p}__${index}`} className="bottom-gutter-tiny">
                                {p || <span>&nbsp;</span>}
                            </p>
                        ))}
                </div>

                {blok.benefits && (
                    <div
                        className={`article-full__benefit bottom-gutter-mid ${dynamicLeftIndent}`}
                    >
                        <div className="article-full__benefit-title infoText">
                            {"Albertine benefit".toUpperCase()}
                        </div>
                        <p className="paragraphItalic">{blok.benefits}</p>
                    </div>
                )}

                <div className="bottom-gutter-widest" />

                {blok.insight && (
                    <div className="article-full__insight">
                        <div className="article-full__insight-title tag">
                            Albertine insight
                        </div>
                        <p className="paragraphMedium">{blok.insight}</p>
                    </div>
                )}

                {place !== undefined && (
                    <div className="article-full__location-container">
                        <div className="article-full__location">
                            <a
                                href={`http://maps.google.com/maps?q=${place.lat},${place.lon}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <div className="article-full__location-title infoText">
                                    {blok.title.toUpperCase()}
                                </div>
                                <div className="paragraph">{place.address}</div>
                                <div className="paragraph highlight-link">
                                    View on Google Maps
                                </div>
                            </a>
                            {blok.website && (
                                <div className="top-gutter-tiny">
                                    <a
                                        href={blok.website}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <div className="paragraph">Learn more</div>
                                        <div className="paragraph highlight-link">
                                            Visit website
                                        </div>
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {separatedTags.length > 0 && (
                    <div className="article-full__tags top-gutter-wide bottom-gutter-wide">
                        {separatedTags.map(([category, tags], index) => {
                            const isLast = index === separatedTags.length - 1;
                            return (
                                <div
                                    key={category}
                                    className={classNames(
                                        "article-full__tag-row",
                                        "bottom-gutter-tiny",
                                        { "border-bottom": !isLast },
                                    )}
                                >
                                    <div className="article-full__tag-category paragraph">
                                        {isTypeOf(category, TagCategory)
                                            ? TagCategoryToDisplayName[category]
                                            : category}
                                    </div>
                                    <div className="article-full__tag-list">
                                        {tags.sort().map((tag, tagIndex) => (
                                            <div key={tag} className="textButton">
                                                {isTypeOf(tag, Tag)
                                                    ? (TagToJSXElement[tag] ??
                                                      tagDisplayName(tag, "en"))
                                                    : tag}
                                                {tagIndex < tags.length - 1 && (
                                                    <span>,&nbsp;</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {!blok.callToAction && (
                <div className="article-full__ctaBlock">
                    <div className="paragraphItalic">
                        Reach out to your lifestyle manager for details and booking
                    </div>
                    <div className="appButton btn link">Enquire about this</div>
                </div>
            )}
        </div>
    );
}

export default FullArticle;
